import { DEFAULT_PLANNED_SAMPLE_SIZE } from "@digital-twins/shared-types";
import { buildStudyBrief } from "@digital-twins/shared-types/study-brief";
import type { ComponentProps } from "react";
import type { ComposeModal } from "@/pages/ConversationsPage/ComposeModal/ComposeModal";
import type {
  ImageSuggestion,
  QuickStudySuggestion,
  RoomSuggestion,
  StudySuggestion,
} from "@digital-twins/shared-types/assistant";

/** Preserve the writer's brief and audience references through the existing creator handoff. */
export function studySuggestionHandoff(
  suggestion: StudySuggestion,
  threadToken: string,
) {
  return {
    initialMessage: buildStudyBrief(suggestion.spec),
    sourceThreadToken: threadToken,
    audienceToken: suggestion.spec.audience.audienceToken,
    populationFilter: suggestion.spec.audience.populationFilter,
    segmentLabel: suggestion.spec.audience.segmentLabel,
    ...(suggestion.spec.goalId ? { goalId: suggestion.spec.goalId } : {}),
  };
}

/** Full Study suggestions retain their existing creator handoff. Quick uses a native turn. */
export function studySuggestionPreparation(suggestion: StudySuggestion) {
  return {title: suggestion.title, spec: {...suggestion.spec, audience: {...suggestion.spec.audience, plannedSampleSize: suggestion.spec.audience.plannedSampleSize ?? DEFAULT_PLANNED_SAMPLE_SIZE}}};
}

/** A launch card's review: its 1:1 or group choice, and the message the room links back to. */
export interface RoomSuggestionLaunch {
  participantCount: number;
  sourceMessageToken?: string;
}

export function roomSuggestionModalProps(
  suggestion: RoomSuggestion,
  picked: { personaTokens: Array<string> },
  threadToken: string,
  language?: string | null,
  launch?: RoomSuggestionLaunch,
): Pick<
  ComponentProps<typeof ComposeModal>,
  | "groupMode"
  | "initialAudienceId"
  | "initialTwinIds"
  | "initialTopics"
  | "initialMessage"
  | "sourceThreadToken"
  | "initialLanguage"
  | "sourceMessageToken"
> {
  return {
    groupMode: launch?.participantCount !== 1,
    initialAudienceId: suggestion.spec.twins.audienceToken,
    initialTwinIds: picked.personaTokens,
    initialTopics: suggestion.spec.topic,
    initialMessage: suggestion.spec.openingQuestion,
    sourceThreadToken: threadToken,
    initialLanguage: language,
    ...(launch?.sourceMessageToken ? { sourceMessageToken: launch.sourceMessageToken } : {}),
  };
}

/** UUID v5 from persisted source identity and the exact typed suggestion. */
export async function suggestionActionId(threadToken: string, sourceMessageToken: string, suggestion: StudySuggestion | QuickStudySuggestion | ImageSuggestion): Promise<string> {
  if (!sourceMessageToken) throw new Error('The suggestion needs its saved source message.')
  const {canonicalJson} = await import('@digital-twins/shared-types/questionnaire/question-dependents')
  const namespace = Uint8Array.from([0x6b, 0x2e, 0xe7, 0xca, 0x90, 0xde, 0x4a, 0x2e, 0xb6, 0xb0, 0xb5, 0x5d, 0x1a, 0xca, 0x99, 0x72])
  const name = new TextEncoder().encode(canonicalJson([threadToken, sourceMessageToken, suggestion]))
  const input = new Uint8Array(namespace.length + name.length)
  input.set(namespace); input.set(name, namespace.length)
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-1', input)).slice(0, 16)
  bytes[6] = (bytes[6] & 0x0f) | 0x50
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
